import { AsyncPipe, DecimalPipe } from '@angular/common'
import { Component, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatButtonModule, MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { Call, CallsService, MetricType } from '../../calls.service'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { InfoDialogComponent } from '../../info-dialog/info-dialog.component'
import { MetricsService } from '../../metrics.service'
import { ToastService } from '../../toast.service'
import { EditorSettingsService, EDITOR_ZOOM_MAX, EDITOR_ZOOM_MIN } from '../editor/editor-settings.service'
import { EditorComponent } from '../editor/editor.component'
import { OutlineComponent } from '../outline/outline.component'
import { TiptapEditorComponent } from '../tiptap-editor/tiptap-editor.component'

@Component({
  selector: 'app-call-outputs-container',
  standalone: true,
  imports: [
    EditorComponent,
    OutlineComponent,
    TiptapEditorComponent,
    MatIcon,
    MatIconButton,
    TranslatePipe,
    TranslateDirective,
    MatTooltip,
    MatMenuModule,
    MatButtonModule,
    AsyncPipe,
    DecimalPipe,
    MatIconButtonSizesModule,
  ],
  templateUrl: './call-outputs-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './call-outputs-container.component.scss',
})
export class CallOutputsContainerComponent {
  @ViewChild(EditorComponent)
  editor!: EditorComponent

  @ViewChild(TiptapEditorComponent)
  tiptapEditor: TiptapEditorComponent | null = null

  @ViewChild(OutlineComponent)
  outlineComponent: OutlineComponent | undefined

  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canUserEdit!: boolean

  @Input({ required: true })
  canUserComment!: boolean

  @Input()
  isSensitiveModeEnabled: boolean = false

  @Output()
  openAgendaUpload = new EventEmitter<void>()

  @Output()
  openMeeting = new EventEmitter<void>()

  @Output()
  openDocument = new EventEmitter<{ resourceId: number; resourcePageNumber: number }>()

  get showExpandAll(): boolean {
    return this.outlineComponent?.showExpandAll() ?? false
  }

  currentTab: 'EDITOR' | 'OUTLINE' = 'EDITOR'

  get showCitations(): boolean {
    return this.editorSettingsService.showCitations
  }

  set showCitations(value: boolean) {
    this.editorSettingsService.setShowCitations(value)
  }

  get hasCitations(): boolean {
    return this.call.minutesCitations !== null
  }

  get canExportCurrentTab(): boolean {
    if (this.currentTab === 'OUTLINE') {
      return this.call.allowOutlineDownload
    }
    // With no minutes the editor content is seeded from the outline, so exporting it
    // requires outline download permission (mirrors the backend download guard).
    return this.call.allowMinutesDownload && (this.call.hasMinutes || this.call.allowOutlineDownload)
  }

  toggleCitations() {
    this.editorSettingsService.setShowCitations(!this.showCitations)
  }

  get showWordCount(): boolean {
    return this.editorSettingsService.showWordCount
  }

  toggleWordCount() {
    this.editorSettingsService.setShowWordCount(!this.showWordCount)
  }

  toggleEditorMaximized() {
    this.editorSettingsService.setIsEditorMaximized(!this.editorSettingsService.isEditorMaximized)
  }

  readonly editorZoomMin = EDITOR_ZOOM_MIN
  readonly editorZoomMax = EDITOR_ZOOM_MAX

  zoomIn() {
    this.editorSettingsService.zoomIn()
  }

  zoomOut() {
    this.editorSettingsService.zoomOut()
  }

  resetZoom() {
    this.editorSettingsService.resetZoom()
  }

  relayOpenDocument(event: { resourceId: number; resourcePageNumber: number }) {
    this.editorSettingsService.setIsEditorMaximized(false)
    this.openDocument.emit(event)
  }

  relayOpenMeeting() {
    this.editorSettingsService.setIsEditorMaximized(false)
    this.openMeeting.emit()
  }

  toastService = inject(ToastService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  metricsService = inject(MetricsService)
  callsService = inject(CallsService)
  editorSettingsService = inject(EditorSettingsService)
  isEditorMaximized$ = this.editorSettingsService.isEditorMaximized$
  zoomLevel$ = this.editorSettingsService.zoomLevel$
  isEditorLockedByOtherUser = true

  async resetEditor() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('EDITOR.RESET_DIALOG_TITLE'),
        description: this.translateService.instant('EDITOR.RESET_DIALOG_DESCRIPTION'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        if (this.call.useLegacyEditor) {
          await this.editor.reset()
        } else {
          if (this.tiptapEditor === null) {
            return
          }
          await this.tiptapEditor.reset()
        }
      }
    })
  }

  showRevisionHistory() {
    this.editor.showRevisionHistory()
  }

  switchTab(tab: 'EDITOR' | 'OUTLINE') {
    this.currentTab = tab
    if (tab === 'OUTLINE') {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_ON_OUTLINE_TAB)
    }
  }

  async exportDocument(format: 'pdf' | 'docx') {
    const contentType = this.currentTab === 'EDITOR' ? 'minutes' : 'outline'
    const infoDialogRef = this.dialog.open(InfoDialogComponent, {
      width: '400px',
      data: {
        title: this.translateService.instant(contentType === 'minutes' ? 'CALLS.MINUTES_EXPORT_DIALOG_TITLE' : 'CALLS.OUTLINE_EXPORT_DIALOG_TITLE'),
        displayLoadingSpinner: true,
        displayConfirmButton: false,
      },
    })
    const export$ = contentType === 'minutes' ? this.callsService.exportMinutes(this.call, format) : this.callsService.exportOutline(this.call, format)
    const subscription = export$.subscribe({
      error: () => {
        this.toastService.show('CALLS.EXPORT_FAILED')
        infoDialogRef.close()
      },
      complete: () => {
        infoDialogRef.close()
      },
    })
    infoDialogRef.afterClosed().subscribe(() => {
      subscription.unsubscribe()
    })
  }

  expandAll() {
    this.outlineComponent?.expandAll()
  }

  collapseAll() {
    this.outlineComponent?.collapseAll()
  }
}
